import { ReactNode } from 'react';
import HomeV2Background from '../home-v2/HomeV2Background.js';
import HomeV2Header, { HomeV2HeaderPlaceholder } from '../home-v2/HomeV2Header.js';
import HomeV2Footer, { HomeV2FooterPlaceholder } from '../home-v2/HomeV2Footer.js';
import type { NavItemId } from '../home-v2/HomeV2Footer.js';
import type { TimeOfDayConfig } from '../home-v2/timeOfDayConfig.js';

interface CategoryDetailLayoutProps {
  children?: ReactNode;
  /** Footer tab to highlight. Pass "journey" for category detail screens. */
  activeTab?: NavItemId;
  /** Time-of-day theming for header background color and gradient. */
  timeOfDayConfig?: TimeOfDayConfig;
  /** Show the cloud mascot in the header. */
  showCloudLogo?: boolean;
  /** Show the user avatar in the header. */
  showAvatar?: boolean;
  /** Click handler for the cloud mascot (check-in flow entry point). */
  onCloudClick?: () => void;
}

/**
 * Dedicated layout for CategoryDetailPage.
 *
 * Structure: fixed header → scrollable gradient background → white card → fixed footer.
 * The white card spans the full viewport width on mobile (max-w-md cap only kicks in
 * on wider screens). No horizontal padding is added — child sections manage their own px-*.
 *
 * Night/morning decorations are always disabled because the white card covers that zone.
 */
export default function CategoryDetailLayout({
  children,
  activeTab,
  timeOfDayConfig,
  showCloudLogo = false,
  showAvatar = true,
  onCloudClick,
}: CategoryDetailLayoutProps) {
  return (
    <div className="min-h-screen relative" style={{ overflow: 'hidden' }}>
      <HomeV2Header
        showCloudLogo={showCloudLogo}
        backgroundColor={timeOfDayConfig?.headerBackground}
        showAvatar={showAvatar}
        onCloudClick={onCloudClick}
      />
      <HomeV2HeaderPlaceholder />

      <HomeV2Background
        timeOfDayConfig={timeOfDayConfig}
        showNightDecorations={false}
        showMorningGlow={false}
      >
        <main className="overflow-x-hidden flex flex-col" style={{ minHeight: '100vh' }}>
          {/*
           * w-full is required alongside max-w-md. Without it, mx-auto suppresses
           * align-self:stretch, so the card sizes to its intrinsic content width (448px)
           * rather than the viewport — overflowing on any screen narrower than 448px.
           * See: docs/issues/2026-05-03-category-detail-overflow-mobile.md
           */}
          <div
            className="w-full max-w-md lg:max-w-[640px] mx-auto"
            style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}
          >
            {children}
            <HomeV2FooterPlaceholder />
          </div>
        </main>
      </HomeV2Background>

      <HomeV2Footer activeTab={activeTab} />
    </div>
  );
}
